import {
  DataBlockDictionaryDto,
  DocumentTemplateType,
  QueryFactory,
  ResourceType,
} from '@lmt/shared-types';
import { CustomDictionaryName } from '@lmt/unicorn-shared/app/api/custom-hooks';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import CalendarIcon from '@lmt/unicorn-shared/app/icons/calendar-settings.svg?react';
import DictionaryIcon from '@lmt/unicorn-shared/app/icons/settings/dictionaries.svg?react';
import ArrowIcon from '@lmt/unicorn-shared/app/icons/arrow-down.svg?react';
import GeneralIcon from '@lmt/unicorn-shared/app/icons/settings/general.svg?react';
import Hl7Icon from '@lmt/unicorn-shared/app/icons/settings/hl7.svg?react';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { UniLink } from '@lmt/unicorn-shared/src/app/components/common/ui-kit/buttons/UniLink';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonColor } from '../common/ui-kit/buttons/Button';
import { useAddDictionaryHandler } from '../dictionaries/useAddDictionaryHandler';
import AddIcon from '@lmt/unicorn-shared/src/app/icons/plus-blue.svg?react';
import styles from './SettingsPage.module.scss';
import { IconButton } from '../common/ui-kit/IconButton';
import { MouseButton } from '../common/types';

export const SettingsPage: React.FC = React.memo(function SettingsPage() {
  const i18n = useScopedTranslation('main_settings');
  const i18nDataBlockFields = useTranslation(NS_DataBlockFields);
  const i18nTabs = useScopedTranslation('tabs');
  const i18nDictionaries = useScopedTranslation(
    'dictionaries',
    NS_DataBlockFields,
  );
  const i18nResources = useScopedTranslation('resources');
  const claims = useClaims();

  const [isShowCustomDictionaries, setShowCustomDictionaries] =
    useState<boolean>(false);

  const customDictionariesQuery =
    QueryFactory.DataBlockDictionaryQuery.useGetQuery({
      select: (data) =>
        data.filter((x) => !x.isDeleted && !x.isExternal && x.isEditable),
      throwOnError: false,
    });

  const { enumFieldLinkProps: addDictionaryHandler } =
    useAddDictionaryHandler();

  const customDictionaries: DataBlockDictionaryDto[] = useMemo(() => {
    const dictionaries = customDictionariesQuery.data ?? [];
    return dictionaries.filter((x) => !x.isMandatory && !x.isReadOnly);
  }, [customDictionariesQuery.data]);

  const mandatoryDictionaries: DataBlockDictionaryDto[] = useMemo(() => {
    const dictionaries = customDictionariesQuery.data ?? [];
    return dictionaries.filter(
      (x) =>
        (x.isMandatory && x.title !== CustomDictionaryName.Sex) || x.isReadOnly,
    );
  }, [customDictionariesQuery.data]);

  return (
    <div className={styles.storedScrolling}>
      <Loading loading={false} flex={true}>
        <div className={styles.container} data-test-id="settings-page">
          <div className={styles.sectionTitle}>
            <GeneralIcon className={styles.headerIcon} />
            <div className={styles.sectionTitleText}>{i18n.t('general')}</div>
          </div>
          <div className={styles.section}>
            {claims.permissions.auditLog && (
              <SingleBlock
                title={i18nTabs.t('audit.title')}
                description={i18nTabs.t('audit.description')}
                page={pages.audit.link()}
              />
            )}

            {claims.permissions.documentTemplateManagement && (
              <>
                <SingleBlock
                  title={i18nTabs.t('documents.title')}
                  description={i18nTabs.t('documents.description')}
                  page={pages.documentDesigner.link({
                    templateType: DocumentTemplateType.Document,
                  })}
                />
                <SingleBlock
                  title={i18nTabs.t('document_headers.title')}
                  description={i18nTabs.t('document_headers.description')}
                  page={pages.documentDesigner.link({
                    templateType: DocumentTemplateType.DocumentHeader,
                  })}
                />
                <SingleBlock
                  title={i18nTabs.t('document_footers.title')}
                  description={i18nTabs.t('document_footers.description')}
                  page={pages.documentDesigner.link({
                    templateType: DocumentTemplateType.DocumentFooter,
                  })}
                />
                <SingleBlock
                  title={i18nTabs.t('document_template_rules.title')}
                  description={i18nTabs.t(
                    'document_template_rules.description',
                  )}
                  page={pages.documentTemplateRules.link()}
                />
              </>
            )}

            {claims.permissions.mergePatients && (
              <SingleBlock
                title={i18nTabs.t('merge_assistant.title')}
                description={i18nTabs.t('merge_assistant.description')}
                page={pages.mergeAssistant.link()}
              />
            )}
            {claims.permissions.statisticsReport && (
              <SingleBlock
                title={i18nTabs.t('statistics.viewer.title')}
                description={i18nTabs.t('statistics.viewer.description')}
                page={pages.statisticsViewer.link()}
              />
            )}
            {claims.permissions.statisticsDesigner && (
              <SingleBlock
                title={i18nTabs.t('statistics.designer.title')}
                description={i18nTabs.t('statistics.designer.description')}
                page={pages.statisticsDesigner.link()}
              />
            )}
            {(claims.permissions.tenantSettingsChange ||
              claims.permissions.hl7Management ||
              claims.permissions.gdtManagement) && (
              <SingleBlock
                title={i18nTabs.t('tenant_settings.title')}
                description={i18nTabs.t('tenant_settings.description')}
                page={pages.tenantSettings.link()}
              />
            )}
            {claims.permissions.createSubtenant && (
              <SingleBlock
                title={i18nTabs.t('subtenant_settings.title')}
                description={i18nTabs.t('subtenant_settings.description')}
                page={pages.subtenantSettings.link()}
              />
            )}
            <SingleBlock
              title={i18nTabs.t('workstation_settings.title')}
              description={i18nTabs.t('workstation_settings.description')}
              page={pages.workstationSettings.link()}
            />
          </div>
          {(claims.permissions.roleManagement ||
            claims.permissions.createSubtenant ||
            claims.permissions.userManagement) && (
            <>
              <div className={styles.sectionTitle}>
                <DictionaryIcon className={styles.headerIcon} />
                <div className={styles.sectionTitleText}>{i18n.t('users')}</div>
              </div>
              <div className={styles.section}>
                {claims.permissions.userManagement && (
                  <SingleBlock
                    title={i18nTabs.t('users_section.users.title')}
                    description={i18nTabs.t('users_section.users.description')}
                    page={pages.users.link()}
                  />
                )}
                {claims.permissions.roleManagement && (
                  <SingleBlock
                    title={i18nTabs.t('users_section.roles.title')}
                    description={i18nTabs.t('users_section.roles.description')}
                    page={pages.roles.link()}
                  />
                )}
                {claims.permissions.createSubtenant && (
                  <SingleBlock
                    title={i18nTabs.t('users_section.subtenants.title')}
                    description={i18nTabs.t(
                      'users_section.subtenants.description',
                    )}
                    page={pages.subtenants.link()}
                  />
                )}
              </div>
            </>
          )}
          <>
            <div className={styles.sectionTitle}>
              <CalendarIcon className={styles.headerIcon} />
              <div className={styles.sectionTitleText}>
                {i18n.t('calendar')}
              </div>
            </div>
            <div className={styles.section}>
              {claims.permissions.calendarSettingsManagement && (
                <>
                  <SingleBlock
                    title={i18nTabs.t('color_settings.title')}
                    description={i18nTabs.t('color_settings.description')}
                    page={pages.calendarColorsSettings.link()}
                  />
                  <SingleBlock
                    title={i18nTabs.t('visit_card_settings.title')}
                    description={i18nTabs.t('visit_card_settings.description')}
                    page={pages.calendarVisitCardSettings.link()}
                  />
                </>
              )}

              <SingleBlock
                title={i18nTabs.t('absences')}
                page={pages.absences.link()}
              />
            </div>
          </>
          {(claims.permissions.doctorManagement ||
            claims.permissions.dictionariesManagement) && (
            <div className={styles.sectionTitle}>
              <DictionaryIcon className={styles.headerIcon} />
              <div className={styles.sectionTitleText}>
                {i18n.t('dictionaries')}
              </div>
            </div>
          )}
          <div className={styles.section}>
            {claims.permissions.doctorManagement && (
              <SingleBlock
                title={i18nDictionaries.t('doctors.title')}
                description={i18nDictionaries.t('doctors.description')}
                page={pages.doctors.link()}
              />
            )}
            {claims.permissions.doctorManagement && (
              <SingleBlock
                title={i18nDictionaries.t('clinic_doctors.title')}
                description={i18nDictionaries.t('clinic_doctors.description')}
                page={pages.attendingDoctors.link()}
              />
            )}
            {claims.permissions.dictionariesManagement && (
              <>
                <SingleBlock
                  title={i18nDictionaries.t('tags.title')}
                  description={i18nDictionaries.t('tags.description')}
                  page={'/dictionaries/tags'}
                />

                <SingleBlock
                  title={i18nDictionaries.t('visit_types.title')}
                  description={i18nDictionaries.t('visit_types.description')}
                  page={'/dictionaries/visit-types'}
                />
                {mandatoryDictionaries.map((dictionary) => (
                  <SingleBlock
                    key={dictionary.title}
                    title={i18nDataBlockFields.t(dictionary.title as any)}
                    page={pages.customDictionary.link({
                      dictionaryId: dictionary.id,
                    })}
                  />
                ))}
              </>
            )}
          </div>

          {claims.permissions.dictionariesManagement && (
            <>
              <div
                className={styles.sectionTitle}
                data-test-id="custom-dictionary-section-title"
              >
                <DictionaryIcon className={styles.headerIcon} />
                <div
                  className={styles.sectionTitleText}
                  onClick={() =>
                    setShowCustomDictionaries(!isShowCustomDictionaries)
                  }
                >
                  {i18n.t('custom_dictionaries')}
                </div>
                <IconButton
                  color="grey"
                  data-up={isShowCustomDictionaries}
                  iconComponent={<ArrowIcon />}
                  className={styles.iconButton}
                  onClick={() =>
                    setShowCustomDictionaries(!isShowCustomDictionaries)
                  }
                />
                {claims.permissions.advancedDictionarySettings && (
                  <UniLink
                    className={styles.addDictionaryButton}
                    icon={<AddIcon />}
                    color={ButtonColor.Primary}
                    title={i18n.t('buttons.add_dictionary')}
                    onClick={addDictionaryHandler.onClick}
                    testId="add-new-dictionary-button"
                  />
                )}
              </div>
              <>
                {isShowCustomDictionaries && (
                  <div
                    className={styles.section}
                    data-test-id="custom-dictionaries-section"
                  >
                    {customDictionariesQuery.isError && (
                      <h1>
                        {convertToErrorString(customDictionariesQuery.error)}
                      </h1>
                    )}
                    {customDictionariesQuery.isLoading && (
                      <Loading loading={true} />
                    )}
                    {customDictionaries
                      .sort((a, b) =>
                        i18nDataBlockFields
                          .t(a.title as any)
                          .localeCompare(i18nDataBlockFields.t(b.title as any)),
                      )
                      .map((dictionary) => (
                        <SingleBlock
                          key={dictionary.title}
                          title={i18nDataBlockFields.t(dictionary.title as any)}
                          page={pages.customDictionary.link({
                            dictionaryId: dictionary.id,
                          })}
                        />
                      ))}
                  </div>
                )}
              </>
            </>
          )}

          {(claims.permissions.editMeasurementMappings ||
            claims.permissions.dataBlockFieldManagement ||
            claims.permissions.deactivateDatablocks ||
            claims.permissions.dataBlockTypeManagement ||
            claims.permissions.configureMeasurementUI) && (
            <>
              <div className={styles.sectionTitle}>
                <DictionaryIcon className={styles.headerIcon} />
                <div className={styles.sectionTitleText}>
                  {i18n.t('data_blocks')}
                </div>
              </div>
              <div className={styles.section}>
                {(claims.permissions.dataBlockFieldManagement ||
                  claims.permissions.configureMeasurementUI) && (
                  <SingleBlock
                    title={i18nTabs.t('data_blocks.fields.title')}
                    description={i18nTabs.t('data_blocks.fields.description')}
                    page={pages.dataBlockFields.link()}
                  />
                )}
                {(claims.permissions.dataBlockTypeManagement ||
                  claims.permissions.configureMeasurementUI ||
                  claims.permissions.deactivateDatablocks) && (
                  <SingleBlock
                    title={i18nTabs.t('data_blocks.types.title')}
                    description={i18nTabs.t('data_blocks.types.description')}
                    page={pages.dataBlockTypes.link()}
                  />
                )}
                {claims.permissions.editMeasurementMappings && (
                  <SingleBlock
                    title={i18nTabs.t('data_blocks.mappings.title')}
                    description={i18nTabs.t('data_blocks.mappings.description')}
                    page={pages.mappings.link()}
                  />
                )}
                {claims.permissions.configureMeasurementUI && (
                  <SingleBlock
                    title={i18nTabs.t('data_blocks.settings.title')}
                    description={i18nTabs.t('data_blocks.settings.description')}
                    page={pages.dataBlockSettings.link()}
                  />
                )}
              </div>
            </>
          )}

          {claims.permissions.hl7Management && (
            <>
              <div className={styles.sectionTitle}>
                <Hl7Icon className={styles.headerIcon} />
                <div className={styles.sectionTitleText}>{i18n.t('hl7')}</div>
              </div>
              <div className={styles.section}>
                <SingleBlock
                  title={i18nTabs.t('hl7.messages.title')}
                  description={i18nTabs.t('hl7.messages.description')}
                  page={pages.hl7Messages.link()}
                />
                <SingleBlock
                  title={i18nTabs.t('hl7.filters.title')}
                  description={i18nTabs.t('hl7.filters.description')}
                  page={pages.hl7Filters.link()}
                />
                <SingleBlock
                  title={i18nTabs.t('hl7.mapping.title')}
                  description={i18nTabs.t('hl7.mapping.description')}
                  page={pages.hl7Mappings.link()}
                />
                <SingleBlock
                  title={i18nTabs.t('hl7.message_types.title')}
                  description={i18nTabs.t('hl7.message_types.description')}
                  page={pages.hl7MessageTypes.link()}
                />
                <SingleBlock
                  title={i18nTabs.t('hl7.outgoing_templates.title')}
                  description={i18nTabs.t('hl7.outgoing_templates.description')}
                  page={pages.hl7OutgoingTemplates.link()}
                />
                <SingleBlock
                  title={i18nTabs.t('hl7.template_rules.title')}
                  description={i18nTabs.t('hl7.template_rules.description')}
                  page={pages.hl7TemplateRules.link()}
                />
              </div>
            </>
          )}
          {claims.permissions.gdtManagement && (
            <>
              <div className={styles.sectionTitle}>
                <Hl7Icon className={styles.headerIcon} />
                <div className={styles.sectionTitleText}>{i18n.t('gdt')}</div>
              </div>
              <div className={styles.section}>
                <SingleBlock
                  title={i18nTabs.t('gdt.messages.title')}
                  description={i18nTabs.t('gdt.messages.description')}
                  page={pages.gdtMessages.link()}
                />
                <SingleBlock
                  title={i18nTabs.t('gdt.outgoing_templates.title')}
                  description={i18nTabs.t('gdt.outgoing_templates.description')}
                  page={pages.gdtOutgoingTemplates.link()}
                />
                <SingleBlock
                  title={i18nTabs.t('gdt.mapping.title')}
                  description={i18nTabs.t('gdt.mapping.description')}
                  page={pages.gdtMappings.link()}
                />
                <SingleBlock
                  title={i18nTabs.t('gdt.template_rules.title')}
                  description={i18nTabs.t('gdt.template_rules.description')}
                  page={pages.gdtTemplateRules.link()}
                />
              </div>
            </>
          )}
          {claims.permissions.manageResources && (
            <>
              <div className={styles.sectionTitle}>
                <DictionaryIcon className={styles.headerIcon} />
                <div className={styles.sectionTitleText}>
                  {i18n.t('resources')}
                </div>
              </div>
              <div className={styles.section}>
                <SingleBlock
                  title={i18nResources.t('place.title')}
                  description={i18nResources.t('place.description')}
                  page={pages.resources.link({
                    resourceType: ResourceType.Places,
                  })}
                />
                <SingleBlock
                  title={i18nResources.t('special_conditions.title')}
                  description={i18nResources.t(
                    'special_conditions.description',
                  )}
                  page={pages.resources.link({
                    resourceType: ResourceType.SpecialConditions,
                  })}
                />
                <SingleBlock
                  title={i18nResources.t('doctors.title')}
                  description={i18nResources.t('doctors.description')}
                  page={pages.resources.link({
                    resourceType: ResourceType.Doctors,
                  })}
                />
              </div>
            </>
          )}
        </div>
      </Loading>
    </div>
  );
});

type SingleBlockProps = {
  title: string;
  description?: string;
  page: string;
};
const SingleBlock: React.FC<SingleBlockProps> = (props) => {
  const tabs = useTabs();

  return (
    <div
      className={styles.singleBlockWrapper}
      onClick={() => tabs.openTab(props.page)}
      onMouseDown={(e: any) => {
        if (e.button === MouseButton.Middle) {
          e.preventDefault();
          tabs.createTabInBackground(props.page);
        }
      }}
      data-test-id="settings-single-item"
    >
      <div className={styles.title}>{props.title}</div>
      <div className={styles.description}>{props.description}</div>
    </div>
  );
};
